import { Trans } from '@lingui/react/macro';

import { useDirectVendorViewThrough } from '@/api/vendor';
import { SimpleSankeyDiagram } from '@/components/charts/SimpleSankeyDiagram';
import { Empty } from '@/components/Empty';
import { usePageFilterContext } from '@/components/page/PageFilters';
import { Panel } from '@/components/panels';
import { SupportLink } from '@/components/typography';

type Props = {
  isLoading: boolean;
};

const VendorDirectViewThroughSankey = ({ isLoading: isVendorLoading }: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const {
    viewThroughData,
    isOnlyDirectTraffic,
    isLoading: isViewThroughLoading,
    error,
  } = useDirectVendorViewThrough(startDate, endDate, audience?.id);

  const isLoading = isViewThroughLoading || isVendorLoading;
  const nodes = viewThroughData?.nodes;
  const links = viewThroughData?.links;

  return (
    <Panel
      title={<Trans>View-Through Reallocation</Trans>}
      infoPopover={{
        body: (
          <Trans>
            View-Through Reallocation helps attribute a portion of visits originally labeled as
            "Direct" to marketing channels that influenced the user through ad impressions (not
            clicks). This provides a more accurate view of channel performance by crediting visits
            that were likely driven by unclicked, yet seen, campaigns.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="DIRECT_VIEW_THROUGH_REALLOCATION"
            >
              Learn more
            </SupportLink>
          </Trans>
        ),
      }}
      style={{ height: 483 }}
      noPadding
    >
      {isOnlyDirectTraffic ? (
        <Empty>
          <Trans>
            Direct Reallocation is only possible through the use of Channel99 Pixels or Partner
            Integrations.
            <br />
            <SupportLink
              variant="body2"
              color="aqua"
              weight="regular"
              path="DIRECT_VIEW_THROUGH_REALLOCATION"
            >
              Learn more
            </SupportLink>
          </Trans>
        </Empty>
      ) : (
        <SimpleSankeyDiagram
          error={error}
          isLoading={isLoading}
          nodes={nodes}
          links={links}
          emptyMessage={<Trans>No Direct Traffic Available</Trans>}
        />
      )}
    </Panel>
  );
};

export default VendorDirectViewThroughSankey;
